import React from 'react';

import {useGetUserSiteCountryFromStore} from 'web-app/react/bookings-finance/contexts/currency-formatter-context/hooks/use-get-user-site-country-from-store';
import {getLocaleAndCurrencyFromCountry} from 'web-app/react/bookings-finance/contexts/currency-formatter-context/utils/get-locale-and-currency-from-country';

type FormatAmountProps = {
    amount: number;
    withCompactFormat?: boolean;
};

type CurrencyFormatterContextType = {
    formatAmount: (props: FormatAmountProps) => string;
    siteLocale: ReturnType<typeof getLocaleAndCurrencyFromCountry>['locale'];
    siteCurrency: ReturnType<typeof getLocaleAndCurrencyFromCountry>['currency'];
};

const CurrencyFormatterContext = React.createContext<CurrencyFormatterContextType | undefined>(undefined);

type CurrencyFormatterProviderType = {
    children: React.ReactNode;
    siteSetId: string;
};

/**
 * Provider component that makes currency formatting available to all child components
 *
 * Uses the institution's country to determine the appropriate locale and currency format.
 * Provides a formatAmount function through context that automatically formats monetary values
 * according to the locale's conventions.
 *
 * @example
 * ```tsx
 * <CurrencyFormatterProvider siteSetId="123">
 *   <YourComponent />
 * </CurrencyFormatterProvider>
 * ```
 */
export const CurrencyFormatterProvider = ({siteSetId, children}: CurrencyFormatterProviderType) => {
    const siteCountry = useGetUserSiteCountryFromStore({userContextId: siteSetId});

    const siteLocaleAndCurrency = React.useMemo(() => getLocaleAndCurrencyFromCountry(siteCountry), [siteCountry]);

    /**
     * Format a monetary amount according to the locale and currency conventions
     *
     * @param {FormatAmountProps} options - Formatting options
     * @param {number} options.amount - The monetary amount to format
     * @param {boolean} [options.hasCompactFormat=false] - Whether to use compact notation for large numbers
     * @returns {string} Formatted amount with currency symbol
     */
    const formatAmount = React.useCallback(
        ({amount, withCompactFormat = false}: FormatAmountProps) => {
            const notation = withCompactFormat ? 'compact' : 'standard';
            return new Intl.NumberFormat(siteLocaleAndCurrency.locale, {
                style: 'currency',
                currency: siteLocaleAndCurrency.currency,
                notation,
            }).format(amount);
        },
        [siteLocaleAndCurrency],
    );

    const contextValue = React.useMemo(
        () => ({
            formatAmount,
            siteLocale: siteLocaleAndCurrency.locale,
            siteCurrency: siteLocaleAndCurrency.currency,
        }),
        [formatAmount, siteLocaleAndCurrency],
    );

    return <CurrencyFormatterContext.Provider value={contextValue}>{children}</CurrencyFormatterContext.Provider>;
};

/**
 * Hook to access the currency formatter functionality
 *
 * @returns {CurrencyFormatterContextType} Object containing the formatAmount function
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { formatAmount } = useCurrencyFormatter();
 *   return <div>{formatAmount({ amount: 1234.56 })}</div>;
 * }
 * ```
 *
 * @throws {Error} If used outside of a CurrencyFormatterProvider
 */
export const useCurrencyFormatter = () => {
    const context = React.useContext(CurrencyFormatterContext);

    if (context === undefined) {
        throw new Error('useCurrencyFormatter must be used within a CurrencyFormatterProvider');
    }

    return context;
};
